import { Component, OnInit, signal } from '@angular/core';
import { ThemeService } from './core/services/theme.service';
import { RouterOutlet } from '@angular/router';
import { NgClass } from '@angular/common';
import { ResponsiveHelperComponent } from './shared/components/responsive-helper/responsive-helper.component';
import { NgxSonnerToaster } from 'ngx-sonner';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { AppState } from './store/app.store';
import { AuthService } from '@auth0/auth0-angular';
import { login, logout, setUser } from './store/actions/auth.actions';
import { TranslationService } from './core/services/translation.service';
import { selectCurrentLanguage } from './store/selectors/language.selectors';
import { take, switchMap, map, catchError } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { LanguageService } from './core/services/language.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [NgClass, RouterOutlet, ResponsiveHelperComponent, NgxSonnerToaster],
})
export class AppComponent implements OnInit {
  title = 'Limitless Parking';
  dir = signal('ltr');
  lang = signal('en');

  constructor(
    private auth: AuthService,
    private translationService: TranslationService,
    public themeService: ThemeService,
    private translate: TranslateService,
    private store: Store<AppState>,
    private languageService: LanguageService
  ) {
    const language = localStorage.getItem('lng');
    const dir = localStorage.getItem('dir');
    if (language && dir) {
      this.dir.set(dir);
      this.lang.set(language);
      this.translate.setDefaultLang(language);
      this.translate.use(language);
      this.translationService.changeLang(language);
    } else {
      this.dir.set('ltr');
      this.lang.set('en');
      this.translate.setDefaultLang('en');
      this.translate.use('en');
      this.translationService.changeLang('en');
    }

    this.store.select(selectCurrentLanguage).subscribe(language => {
      if (language) {
        const dir = language === 'ar' ? 'rtl' : 'ltr';
        this.dir.set(dir);
        this.lang.set(language);
      }
    });
  }

  async ngOnInit(): Promise<void> {
    // Initialize language service and await completion
    await this.languageService.initialize();
    
    // Check if we're already handling auth
    const isHandlingAuth = sessionStorage.getItem('handling_auth');
    if (isHandlingAuth) {
      return;
    }

    // Set handling auth flag in sessionStorage
    sessionStorage.setItem('handling_auth', 'true');

    // Initialize authentication using Auth0's SDK
    this.initAuth();
  }

  private initAuth(): void {
    // First, check if we already have a valid token in localStorage
    const authData = JSON.parse(localStorage.getItem('auth') || '{}');
    if (authData.token) {
      try {
        const decoded = jwtDecode<any>(authData.token);
        const currentTime = Math.floor(Date.now() / 1000);
        if (decoded.exp && decoded.exp > currentTime) {
          // Token is valid, dispatch login action
          if (authData.user && authData.user['org_id']) {
            this.store.dispatch(login({ user: authData.user, token: authData.token }));
            sessionStorage.removeItem('handling_auth');
            return;
          }
        }
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }

    // If no valid token in localStorage, proceed with Auth0 flow
    this.auth.isAuthenticated$.pipe(
      take(1),
      switchMap(isAuthenticated => {
        if (isAuthenticated) {
          return this.auth.user$.pipe(
            take(1),
            switchMap(user => {
              // Only get token if we don't have one in localStorage
              const authData = JSON.parse(localStorage.getItem('auth') || '{}');
              if (authData.token) {
                if (user && user['org_id']) {
                  this.store.dispatch(login({ user, token: authData.token }));
                } else {
                  this.handleLoginRedirect();
                }
                sessionStorage.removeItem('handling_auth');
                return EMPTY;
              }
              
              // Let Auth0 SDK manage tokens using getAccessTokenSilently
              return this.auth.getAccessTokenSilently().pipe(
                take(1),
                map(token => {
                  if (user && user['org_id']) {
                    // Dispatch login action with user and token
                    this.store.dispatch(login({ user, token }));
                  } else {
                    this.handleLoginRedirect();
                  }
                  // Clear handling auth flag
                  sessionStorage.removeItem('handling_auth');
                })
              );
            }),
            catchError(() => {
              this.handleLoginRedirect();
              return EMPTY;
            })
          );
        } else {
          this.handleLoginRedirect();
          return EMPTY;
        }
      })
    ).subscribe({
      error: () => {
        this.handleLoginRedirect();
      }
    });
  }

  private handleLoginRedirect(): void {
    // Clean up session storage
    sessionStorage.removeItem('handling_auth');
    
    // Dispatch logout action
    this.store.dispatch(logout());
    
    // Use Auth0's SDK for login redirect
    this.auth.loginWithRedirect({
      appState: { 
        returnTo: window.location.pathname 
      },
      authorizationParams: {
        prompt: 'login',
        display: 'popup',
        response_type: 'token id_token',
      }
    });
  }
}
